import { useState } from 'react';
import { useData } from '../../../contexts/DataContext';

/**
 * useAccountRefresh - Business logic hook for refreshing account data
 * 
 * Handles account refresh operations with loading states and error handling.
 * Follows separation of concerns by keeping business logic separate from UI.
 */
export const useAccountRefresh = () => {
	const { refreshAccountData } = useData();
	const [refreshingAccounts, setRefreshingAccounts] = useState(new Set());
	const [error, setError] = useState(null);

	const refreshAccount = async (accountId, clientId) => {
		// Add account to refreshing set
		setRefreshingAccounts(prev => new Set(prev).add(accountId));
		setError(null);

		try {
			await refreshAccountData(accountId);
			console.log(`Account ${accountId} refreshed successfully`);
			return { success: true };
		} catch (err) {
			console.error(`Failed to refresh account ${accountId}:`, err);
			setError(err.message);
			return { success: false, error: err.message };
		} finally {
			// Remove account from refreshing set
			setRefreshingAccounts(prev => {
				const newSet = new Set(prev);
				newSet.delete(accountId);
				return newSet;
			});
		}
	};

	const refreshAllClientAccounts = async (clientId, accounts) => {
		setError(null);
		const results = [];

		for (const account of accounts) {
			const result = await refreshAccount(account._id, clientId);
			results.push({ accountId: account._id, ...result });
		}

		const successCount = results.filter(r => r.success).length;
		const failureCount = results.filter(r => !r.success).length;

		return {
			success: failureCount === 0,
			successCount,
			failureCount,
			results
		};
	};

	const isRefreshing = (accountId) => {
		return refreshingAccounts.has(accountId);
	};

	const clearError = () => {
		setError(null);
	};

	return {
		refreshAccount,
		refreshAllClientAccounts,
		isRefreshing,
		refreshingAccounts,
		error,
		clearError
	};
}; 